import { Response } from '@/api'
import api from '@/modules/product/api'
import { ProductSearchOptions } from '@/modules/product/types'
import useProductSearch from '@/modules/product/useProductSearch'
import { realtimePricesBatchedQuery } from '@/modules/product/useRealtimePricesColada'
import { ColadaPageableResponse, PageableResponse } from '@/types'
import { MediumProduct, ProductPriceUpdate } from '@/types/index'
import {
  defineInfiniteQueryOptions,
  defineQuery,
  defineQueryOptions,
  useInfiniteQuery,
  useQueryCache
} from '@pinia/colada'
import { cloneDeep } from 'lodash-es'
import { Ref, ref, watch } from 'vue'

const productSearchWithRealtimeQuery = defineQuery(() => {
  const queryCache = useQueryCache()

  const searchProductOptions = ref<ProductSearchOptions>()

  const productSearchQueryOptions = productSearchInfiniteQueryOptions(
    searchProductOptions as Ref<ProductSearchOptions>
  )
  const { data, loadNextPage, hasNextPage, asyncStatus, status } =
    useInfiniteQuery(productSearchQueryOptions)

  const {
    data: realtimePricesData,
    params: realtimePricesQueryOptionsParams,
    refresh: refreshRealtimePrices
  } = realtimePricesBatchedQuery()

  watch(searchProductOptions, (opts) => {
    useProductSearch().setKeywordTooShortError(
      Boolean(opts?.keyword) && isSearchKeyTooShort(searchProductOptions)
    )
  })

  watch(
    () => {
      if (status.value !== 'success' || !data.value || searchProductOptions.value === undefined) {
        return undefined
      }

      const lastPage = data.value.pages[data.value.pageParams.length - 1]
      if (!lastPage || !lastPage.data?.items || lastPage.data.items.length === 0) {
        return undefined
      }

      return {
        ids: lastPage.data.items.map((item) => item.productId),
        filters: searchProductOptions.value.filters
      }
    },
    (realtimeParams, previousRealtimeParams) => {
      if (!realtimeParams) {
        return
      }

      const hasSameIdsAndFilters =
        JSON.stringify(previousRealtimeParams?.ids) === JSON.stringify(realtimeParams.ids) &&
        JSON.stringify(previousRealtimeParams?.filters) === JSON.stringify(realtimeParams.filters)
      if (hasSameIdsAndFilters) {
        return
      }

      realtimePricesQueryOptionsParams.value = {
        ids: new Set(realtimeParams.ids),
        filters: realtimeParams.filters
      }

      refreshRealtimePrices()
    },
    { deep: true }
  )

  watch(
    () => realtimePricesData.value,
    (newRealtimePricesData) => {
      if (
        realtimePricesQueryOptionsParams.value === undefined ||
        realtimePricesQueryOptionsParams.value.ids.size === 0 ||
        !newRealtimePricesData ||
        !searchProductOptions.value
      ) {
        return
      }

      queryCache.setQueryData<
        ColadaPageableResponse<Response<PageableResponse<MediumProduct>>> | undefined
      >(
        PRODUCT_SEARCH_QUERY_KEYS.searchOptions(searchProductOptions.value),
        (
          productSearchEntry:
            ColadaPageableResponse<Response<PageableResponse<MediumProduct>>> | undefined
        ): ColadaPageableResponse<Response<PageableResponse<MediumProduct>>> | undefined => {
          if (!productSearchEntry) {
            return undefined
          }

          const clonedProductSearchEntry = cloneDeep(productSearchEntry)

          const pageParam =
            clonedProductSearchEntry.pageParams[clonedProductSearchEntry.pageParams.length - 1]
          newRealtimePricesData.data.forEach((realtimeProduct) => {
            const index = clonedProductSearchEntry.pages[pageParam].data.items.findIndex(
              (item) => item.productId === realtimeProduct.productId
            )
            if (index !== undefined && index >= 0) {
              const item = clonedProductSearchEntry.pages[pageParam].data.items[index]
              if (item) {
                item.price = realtimeProduct.price
                item.stock = realtimeProduct.stock
                const suppliers = (realtimeProduct as ProductPriceUpdate).suppliers
                if (suppliers) item.suppliers = suppliers
              }
            }
          })
          return clonedProductSearchEntry
        }
      )
    }
  )

  // watch for isLoading to finish
  // -> call realtime prices query for all products in the last page

  return {
    searchProductOptions,
    data,
    loadNextPage,
    hasNextPage,
    asyncStatus,
    status
  }
})

const PRODUCT_SEARCH_QUERY_KEYS = {
  root: ['productSearch'] as const,
  searchOptions: (options: ProductSearchOptions) =>
    [...PRODUCT_SEARCH_QUERY_KEYS.root, options] as const
}

const productSearchQueryOptions = defineQueryOptions(
  ({ searchProductOptions }: { searchProductOptions: ProductSearchOptions }) => ({
    key: PRODUCT_SEARCH_QUERY_KEYS.searchOptions(searchProductOptions),
    query: () => api.searchProducts(searchProductOptions, 0)
  })
)

const productSearchInfiniteQueryOptions = defineInfiniteQueryOptions(
  (searchProductOptions: Ref<ProductSearchOptions>) => ({
    key: () => PRODUCT_SEARCH_QUERY_KEYS.searchOptions(searchProductOptions.value),
    initialPageParam: 0,
    enabled: () => {
      const opts = searchProductOptions.value
      if (!opts) return false
      const hasSearchTerm = Boolean(
        searchProductOptions.value.keyword || searchProductOptions.value.category
      )
      return hasSearchTerm && !isSearchKeyTooShort(searchProductOptions)
    },
    query: ({ pageParam }: { pageParam: number }) => {
      useProductSearch().setKeywordTooShortError(false)
      return api.searchProducts(searchProductOptions.value, pageParam)
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage) {
        return undefined
      }
      return lastPage.config.params.size * (lastPage.config.params.page + 1) <
        lastPage.data.totalCount
        ? lastPage.config.params.page + 1
        : undefined
    },
    meta: () => ({
      onError: {
        defaultConfigParams: isSearchKeyTooShort(searchProductOptions)
          ? {
              appearance: 'NONE'
            }
          : undefined
      }
    })
  })
)

const isSearchKeyTooShort = (searchProductOptions: Ref<ProductSearchOptions | undefined>) => {
  return (
    !searchProductOptions.value?.category &&
    (!searchProductOptions.value?.keyword || searchProductOptions.value.keyword.length <= 1)
  )
}

export default productSearchWithRealtimeQuery
export {
  PRODUCT_SEARCH_QUERY_KEYS,
  productSearchInfiniteQueryOptions,
  productSearchQueryOptions,
  productSearchWithRealtimeQuery
}
